import type { FilePondFile } from 'filepond';
import type { FilePond } from 'react-filepond';
import { useCallback, useRef, type SetStateAction } from 'react';
import { handleErrorToast } from 'src/components/molecules/Toast/Toast';
import { mapPondFilesToEvidenceUploadFiles } from './mapPondFilesToEvidenceUploadFiles';
import type { DynamicQuestionAnswerValue, DynamicQuestionData } from './types';
import { FILEPOND_PROCESSING_COMPLETE } from './DynamicQuestionProps';

type PondParams = {
  question: DynamicQuestionData;
  value: DynamicQuestionAnswerValue;
  onChange: (value: DynamicQuestionAnswerValue) => void;
  readOnly: boolean;
  onRegisterQuestionnaireAttachment?: (input: {
    questionId: string;
    evidenceS3Path: string;
  }) => Promise<string | undefined>;
  onDeleteQuestionnaireAttachment?: (
    input: { attachmentId: string; questionId: string },
    options?: { onError?: (error: unknown) => void },
  ) => Promise<void>;
};

export function useDynamicQuestionEvidencePond({
  question,
  value,
  onChange,
  readOnly,
  onRegisterQuestionnaireAttachment,
  onDeleteQuestionnaireAttachment,
}: PondParams) {
  const pondRef = useRef<FilePond | null>(null);
  const registeringSourcesRef = useRef(new Set<string>());
  const valueRef = useRef(value);
  valueRef.current = value;

  const syncEvidenceFilesFromPond = useCallback(() => {
    const pond = pondRef.current;
    if (pond == null) return;
    const newFiles = mapPondFilesToEvidenceUploadFiles(pond);
    const base = valueRef.current;
    onChange({
      ...base,
      evidenceUploadFiles: newFiles,
      fileNames: newFiles.map((f) => f.options.file.name),
      uploadedFiles: undefined,
    });

    const questionId = question.id != null ? String(question.id) : '';
    if (readOnly || !questionId || onRegisterQuestionnaireAttachment == null) {
      return;
    }

    void (async () => {
      const pondFiles = pond.getFiles() as FilePondFile[];
      for (const item of pondFiles) {
        const sid = String(item.serverId ?? '').replaceAll('"', '');
        if (!sid || item.status !== FILEPOND_PROCESSING_COMPLETE) continue;
        const existing = item.getMetadata('qaAttachmentId') as
          | string
          | undefined;
        if (existing) continue;
        if (registeringSourcesRef.current.has(sid)) continue;
        registeringSourcesRef.current.add(sid);
        try {
          const attachmentId = await onRegisterQuestionnaireAttachment({
            questionId,
            evidenceS3Path: sid,
          });
          if (attachmentId) {
            item.setMetadata('qaAttachmentId', attachmentId);
            const updated = mapPondFilesToEvidenceUploadFiles(pond);
            const b = valueRef.current;
            onChange({
              ...b,
              evidenceUploadFiles: updated,
              fileNames: updated.map((f) => f.options.file.name),
              uploadedFiles: undefined,
            });
          }
        } catch (error) {
          handleErrorToast({
            toastMessage: 'Failed to register file with questionnaire service',
            toastDescription:
              error instanceof Error ? error.message : String(error),
          });
        } finally {
          registeringSourcesRef.current.delete(sid);
        }
      }
    })();
  }, [onChange, onRegisterQuestionnaireAttachment, question.id, readOnly]);

  const handleRemoveFileFromPond = useCallback(
    (pondFile: FilePondFile) => {
      const attachmentId = pondFile.getMetadata('qaAttachmentId') as
        | string
        | undefined;
      const questionId = question.id != null ? String(question.id) : '';
      if (
        !attachmentId?.trim() ||
        !questionId ||
        !onDeleteQuestionnaireAttachment
      ) {
        return;
      }
      void onDeleteQuestionnaireAttachment(
        { attachmentId, questionId },
        {
          onError: (error) => {
            handleErrorToast({
              toastMessage: 'Failed to remove file from questionnaire service',
              toastDescription:
                error instanceof Error ? error.message : String(error),
            });
          },
        },
      );
    },
    [onDeleteQuestionnaireAttachment, question.id],
  );

  const handleSetPond = useCallback(
    (instance: SetStateAction<FilePond | null>) => {
      pondRef.current =
        typeof instance === 'function' ? instance(pondRef.current) : instance;
    },
    [],
  );

  return {
    syncEvidenceFilesFromPond,
    handleRemoveFileFromPond,
    handleSetPond,
  };
}
